import { IconButton, Page } from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { articles } from 'utils/elevio';

import { FormInfoBanner } from '../../../../components/formInfoBanner';
import { CustomForm } from '../../../../customForms.types';
import { useCustomForms } from '../../../../hooks/useCustomForms/useCustomForms';
import { FormValues } from '../../types';

import { ActionButtons } from './formActionButtons';
import { FormFields } from './formFields/formFields';
import { FormPreviewModal } from './previewModal';

function formatFields(values: FormValues): CustomForm['fields'] {
  return values.formFields.map((field) => ({
    title: field.title,
    placeholder: field.placeholder,
    id: field.id,
    type: field.type,
    required: field.required,
    options: (field.options?.length ?? 0) > 0 ? field.options : undefined,
  }));
}

type Props = {
  form: CustomForm;
  hasAnyEndpointAssociatedWithForm: boolean;
};

export function EditFormSection({
  form,
  hasAnyEndpointAssociatedWithForm,
}: Props): JSX.Element {
  const formState = useFormContext<FormValues>();
  const [isFormPreviewModalOpen, setIsFormPreviewModalOpen] = useState(false);

  const notify = useNotifications();
  const { updateForm } = useCustomForms();

  const onSubmit = async (values: FormValues): Promise<void> => {
    const result = await updateForm({
      id: form.id,
      usage: [], // usage is never updated on client side
      title: form.title,
      fields: formatFields(values),
      submitButtonLabel: values.submitButtonLabel,
      feedbackAfterSubmit: values.feedbackAfterSubmit,
      hasAttributes: values.formFields.length > 0,
    });

    if (result.ok) {
      notify(
        {
          title: 'Form updated',
          time: 5000,
          icon: 'done',
          className: 'success',
        },
        NotificationType.Success,
      );

      // reset the dirty state
      formState.reset({}, { keepValues: true });

      return;
    }

    notify(
      {
        title: 'Could not save the form',
        text: result.error,
        icon: 'exclamation',
        className: 'danger',
        time: 5000,
      },
      NotificationType.Error,
    );
  };

  function openFormsDocumentation(): void {
    elevioClient.openArticle(articles.settings.customForms.documentation);
  }

  return (
    <form onSubmit={formState.handleSubmit(onSubmit)}>
      <Page.Section
        title="Edit form"
        titleMeta={
          <IconButton
            icon="question"
            variant="tertiary"
            onClick={openFormsDocumentation}
          />
        }
        controls={
          <ActionButtons
            onPreviewRequest={() => setIsFormPreviewModalOpen(true)}
          />
        }
      >
        <FormInfoBanner />
        <FormFields
          preventDeletionOfEmailField={hasAnyEndpointAssociatedWithForm}
        />

        <FormPreviewModal
          isOpen={isFormPreviewModalOpen}
          onClose={() => setIsFormPreviewModalOpen(false)}
        />
      </Page.Section>
    </form>
  );
}
